import {
  AudioMixObserver,
  AudioVideoObserver,
  BrowserBehavior,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  DefaultModality,
  DeviceChangeObserver,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
} from 'amazon-chime-sdk-js'
import { MeetingSessionConfigurationOptions } from '../typings/chime/MeetingSessionConfigurationOptions'
import { GetVoiceSign } from '../typings/live/GetVoiceSign'
import { RTCType } from '../typings/rtc/type'
import logger from '../utils/logger'

import { uuid } from '../utils/uuid'
import { CoreRTC } from './CoreRTC'

/**
 * 浏览器端 Amazon Chime SDK RTC 实现
 * 
 * 基于 Amazon Chime SDK for JavaScript 实现的浏览器端 RTC 类。
 * 适用于在浏览器环境中使用 Amazon Chime 进行实时语音通信。
 * 
 * @example
 * ```typescript
 * const rtc = new BrowserRTC4Chime({
 *   getVoiceSign: async (params) => {
 *     const response = await fetch('/api/get-voice-sign', {
 *       method: 'POST',
 *       body: JSON.stringify(params)
 *     })
 *     return response.json()
 *   },
 *   logLevel: LogLevel.INFO
 * })
 * 
 * // 加入房间
 * await rtc.join({
 *   voiceId: 'voice-id',
 *   userId: 'user-id',
 *   roomId: 'room-id',
 *   type: RTCType.RealseeBrowserChimeRTC
 * })
 * ```
 */
export class BrowserRTC4Chime extends CoreRTC {
  type = RTCType.RealseeBrowserChimeRTC
  joined = false
  micro = false
  voiceId = ''
  userId = ''
  logger: ConsoleLogger
  deviceController: DefaultDeviceController
  meetingSession: DefaultMeetingSession | undefined

  audioInput: MediaDeviceInfo | undefined
  audioOutput: MediaDeviceInfo | undefined

  deviceChangeObserver: DeviceChangeObserver = {}
  audioVideoObserver: AudioVideoObserver = {}
  audioMixObserver?: AudioMixObserver

  private _getVoiceSign: GetVoiceSign = () => {
    throw new Error('方法 getVoiceSign 未实现')
  }

  private _autoPlayConfirm: (opts: { onConfirm: () => void }) => void | undefined

  private $dom: HTMLDivElement = document.createElement('div')
  private $audio: HTMLAudioElement = document.createElement('audio')

  /**
   * 创建 BrowserRTC4Chime 实例
   * 
   * @param options 配置选项
   * @param options.getVoiceSign 获取语音签名的函数，必需
   * @param options.autoPlayConfirm 自动播放确认回调（可选），用于处理浏览器的自动播放策略
   * @param options.logLevel 日志级别（可选），默认为 LogLevel.INFO
   */
  constructor(options: {
    getVoiceSign: GetVoiceSign
    autoPlayConfirm?: (opts: { onConfirm: () => void }) => void
    logLevel?: LogLevel
  }) {
    super()
    // 挂载签名钩子
    this._getVoiceSign = options.getVoiceSign
    // @ts-ignore
    this._autoPlayConfirm = options.autoPlayConfirm
    this._initDom()
    this.logger = new ConsoleLogger('WebRTCLogger', options.logLevel || LogLevel.INFO)
    this.deviceController = new DefaultDeviceController(this.logger)
    this.initObservers()
  }

  /**
   * 初始化 DOM 元素（内部方法）
   * 创建并挂载 audio 标签到页面
   */
  _initDom = () => {
    this.$audio.id = `rs-rtc-${uuid()}`
    this.$dom.id = `rs-rtc-${uuid()}`
    this.$dom.appendChild(this.$audio)
    document.body.appendChild(this.$dom)
  }

  /**
   * 初始化事件监听器
   * 
   * 设置设备变化、音视频事件等监听器
   */
  initObservers = () => {
    // 输入输出设备变化的监听器
    this.deviceChangeObserver = {
      audioInputsChanged: (freshAudioInputDeviceList?: MediaDeviceInfo[]) => {
        this.logger.info(`audioInputsChanged ${typeof freshAudioInputDeviceList}`)
        // An array of MediaDeviceInfo objects
        freshAudioInputDeviceList?.forEach((mediaDeviceInfo) => {
          this.logger.info(`Device ID: ${mediaDeviceInfo.deviceId} Microphone: ${mediaDeviceInfo.label}`)
        })
      },

      audioOutputsChanged: (freshAudioOutputDeviceList?: MediaDeviceInfo[]) => {
        this.logger.info(`audioOutputsChanged ${typeof freshAudioOutputDeviceList}`)
        freshAudioOutputDeviceList?.forEach((mediaDeviceInfo) => {
          this.logger.info(`Device ID: ${mediaDeviceInfo.deviceId} Speaker: ${mediaDeviceInfo.label}`)
        })
      },

      // videoInputsChanged: (freshVideoInputDeviceList: any) => {
      //   this.logger.info(`Video inputs updated: ${freshVideoInputDeviceList}`);
      // },

      audioInputMuteStateChanged: (device: string | MediaStream, muteState: boolean) => {
        this.logger.info(`Device ${device.toString()}  ${muteState ? 'is muted in hardware' : 'is not muted'}`)
      },
    }

    // 流变化监听器
    this.audioVideoObserver = {
      audioVideoDidStart: () => {
        this.logger.info('Started')
      },
      audioVideoDidStop: (sessionStatus: any) => {
        // See the "Stopping a session" section for details.
        this.logger.info(`Stopped with a session status code: ${sessionStatus.statusCode()}`)
      },
      audioVideoDidStartConnecting: (reconnecting: any) => {
        if (reconnecting) {
          // e.g. the WiFi connection is dropped.
          this.logger.info('Attempting to reconnect')
        }
      },
    }

    // 混流检测
    this.audioMixObserver = {
      meetingAudioStreamBecameActive: (activeStream: MediaStream) => {
        // this.logger.info('meetingAudioStreamBecameActive ==>')
        // console.log(activeStream)
        if (this.$audio.paused) {
          // 播放远端流
          const _play = () =>
            this.$audio
              .play()
              .then(() => {})
              .catch((error) => {
                logger.error('播放远端流失败！', error)
                if (typeof this._autoPlayConfirm === 'function') {
                  const onConfirm = () => _play()
                  this._autoPlayConfirm({ onConfirm })
                }
              })
          _play()
        }
      },
      meetingAudioStreamBecameInactive: (inactiveStream: MediaStream) => {
        // this.logger.info('meetingAudioStreamBecameInactive ==>')
        // console.log(inactiveStream)
      },
    }
  }

  /**
   * 加入语音会议
   * @param options
   * @returns
   */
  async join(options: {
    voiceId: string
    userId: string
    roomId: string | number | undefined
    type: RTCType
  }): Promise<boolean> {
    return new Promise(async (success, fail) => {
      this.emit('joinWillStart')
      try {
        // 取签名
        const { sdkAppId, userId, roomId, userSig } = await this._getVoiceSign({
          ...options,
          roomId: options.roomId || 0,
        })
        this.userId = userId
        const { attendence, vendor_wrapper }: MeetingSessionConfigurationOptions = JSON.parse(
          decodeURIComponent(userSig),
        )

        // 初始化配置
        const configuration = new MeetingSessionConfiguration(
          {
            Meeting: vendor_wrapper,
          },
          {
            Attendee: {
              ExternalUserId: attendence.external_user_id,
              AttendeeId: attendence.attendee_id,
              JoinToken: attendence.join_token,
            },
          },
        )

        // 初始化会议会话
        this.meetingSession = new DefaultMeetingSession(configuration, this.logger, this.deviceController)

        // 注册事件
        this._registerRtcEvent()

        // 绑定监听器
        this.meetingSession.audioVideo.addDeviceChangeObserver(this.deviceChangeObserver)
        this.meetingSession.audioVideo.addObserver(this.audioVideoObserver)

        // 绑定播放器
        this.meetingSession.audioVideo.bindAudioElement(this.$audio)

        const observer: AudioVideoObserver = {
          audioVideoDidStart: () => {
            this.meetingSession?.audioVideo.realtimeMuteLocalAudio()
            this.joined = true
            this.emit('joined')
            success(true)
          },
          audioVideoDidStop: () => {
            this.joined = false
            success(false)
          },
        }

        this.meetingSession.audioVideo.addObserver(observer)

        if (this.audioMixObserver) {
          this.meetingSession.audioVideo.addAudioMixObserver(this.audioMixObserver)
        }

        this.meetingSession.audioVideo.setDeviceLabelTrigger(
          async () => await navigator.mediaDevices.getUserMedia({ audio: true, video: false }),
        )

        // 设置输入设备
        try {
          const audioInputDevices = await this.meetingSession.audioVideo.listAudioInputDevices()
          logger.info('audioInputDevices ===>', audioInputDevices)

          const audioInputDeviceId = audioInputDevices[0]?.deviceId || 'default'
          await this.meetingSession.audioVideo.startAudioInput(audioInputDeviceId)
        } catch (e) {
          logger.error(`设置语音输入设备失败:${(e as Error).message}`)
        }

        try {
          const audioOutputDevices = await this.meetingSession.audioVideo.listAudioOutputDevices()
          logger.info('audioOutputDevices ===>', audioOutputDevices)
          const audioOutputDeviceId = audioOutputDevices[0]?.deviceId || 'default'

          await this.meetingSession.audioVideo.chooseAudioOutput(audioOutputDeviceId)
        } catch (e) {
          logger.error(`设置语音输出设备失败:${(e as Error).message}`)
        }

        this.meetingSession.audioVideo.start()
      } catch (e: unknown) {
        this.emit('error', e as Error)
        this.logger.error((e as Error).message)
        success(false)
      }
    })
  }

  /**
   * 注册rtc事件
   */
  _registerRtcEvent = () => {
    const presentAttendeeId = this.meetingSession?.configuration?.credentials?.attendeeId || ''
    if (presentAttendeeId) {
      // 处理音量变化
      const roster = new Map<
        string,
        {
          userId: string
          volume: number | null
          muted: boolean | null
          signalStrength: number | null
        }
      >()

      const getUserVolumes = () => {
        const result: any = []
        roster.forEach((v, k) => {
          result.push({
            userId: v.userId,
            volume: v.muted || v.signalStrength === 0 || !Boolean(v.volume) ? 0 : v.volume,
          })
        })
        return result
      }

      this.meetingSession?.audioVideo.realtimeSubscribeToAttendeeIdPresence(
        (presentAttendeeId: any, present: any, externalUserId: any) => {
          if (!present) {
            roster.delete(presentAttendeeId)
            this.emit('userVolumes', getUserVolumes())
            return
          }

          this.meetingSession?.audioVideo.realtimeSubscribeToVolumeIndicator(
            presentAttendeeId,
            (attendeeId: any, volume: any, muted: any, signalStrength: any) => {
              const baseAttendeeId = new DefaultModality(attendeeId).base()
              if (baseAttendeeId !== attendeeId) {
                // Optional: Do not include the content attendee (attendee-id#content) in the roster.
                // See the "Screen and content share" section for details.
                return
              }

              roster.set(attendeeId, {
                userId: externalUserId || attendeeId,
                volume,
                muted,
                signalStrength,
              })
              this.emit('userVolumes', getUserVolumes())
            },
          )
        },
      )

      // 弱网
      this.meetingSession?.audioVideo.realtimeSubscribeToVolumeIndicator(
        presentAttendeeId,
        (attendeeId: any, volume: any, muted: any, signalStrength: any) => {
          const baseAttendeeId = new DefaultModality(attendeeId).base()
          if (baseAttendeeId === attendeeId) {
            if (signalStrength !== null && signalStrength !== 1) {
              this.emit('weakNetwork')
            }
          }
        },
      )

      // 报错

      this.meetingSession?.audioVideo.realtimeSubscribeToFatalError((error: any) => this.emit('error', error))
    }
  }

  quit(): Promise<boolean> {
    if (!this.joined) return Promise.resolve(true)
    return new Promise((success, fail) => {
      this.joined = false
      const observer: AudioVideoObserver = {
        audioVideoDidStop: async (sessionStatus: any) => {
          const sessionStatusCode = sessionStatus.statusCode()
          this.meetingSession?.deviceController.destroy()
          if (sessionStatusCode === MeetingSessionStatusCode.Left) {
            /*
              - You called meetingSession.audioVideo.stop().
              - When closing a browser window or page, Chime SDK attempts to leave the session.
            */
            this.logger.info('You left the session')
            success(true)
          } else {
            this.logger.info(`Stopped with a session status code: ${sessionStatusCode}`)
            success(true)
          }
        },
      }
      this.meetingSession?.audioVideo.addObserver(observer)
      this.meetingSession?.audioVideo.stop()
    })
  }

  async detectMicro(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const rule = { audio: true, video: false }
      try {
        navigator.mediaDevices
          .getUserMedia(rule)
          .then(() => resolve(true))
          .catch(() => resolve(false))
      } catch (e: unknown) {
        this.logger.error((e as Error).message)
        resolve(false)
      }
    })
  }

  async toggleMicro(flag?: boolean): Promise<boolean> {
    if (flag) {
      this.meetingSession?.audioVideo.realtimeUnmuteLocalAudio()
    } else {
      this.meetingSession?.audioVideo.realtimeMuteLocalAudio()
    }
    const muted = this.meetingSession?.audioVideo.realtimeIsLocalAudioMuted()
    if (muted !== undefined) {
      return flag !== muted
    } else {
      return false
    }
  }

  shock(): void {
    throw new Error('Method not implemented.')
  }
}
